import type { SelectedFace } from "../store";
import { useViewerStore } from "../store";
import { Section } from "./Section";

export function RightPanel() {
  const selected = useViewerStore((s) => s.selectedFace);
  const setSelected = useViewerStore((s) => s.setSelectedFace);

  return (
    <aside
      className="w-80 shrink-0 overflow-y-auto"
      style={{
        background: "white",
        borderLeft: "1px solid var(--color-mist)",
      }}
    >
      <div className="p-6">
        <Section title="Inspect">
          {selected ? (
            <InspectDetails
              face={selected}
              onClear={() => setSelected(null)}
            />
          ) : (
            <InspectEmpty />
          )}
        </Section>
      </div>
    </aside>
  );
}

function InspectEmpty() {
  return (
    <div className="py-10 text-center">
      <div
        className="mx-auto mb-4 rounded-full"
        style={{
          width: "44px",
          height: "44px",
          background: "var(--color-cloud)",
          border: "1px solid var(--color-mist)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "20px",
          color: "var(--color-stone)",
        }}
        aria-hidden
      >
        ⌕
      </div>
      <div
        className="mb-2"
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "15px",
          color: "var(--color-slate)",
        }}
      >
        Click any face to inspect
      </div>
      <p
        style={{
          fontSize: "11px",
          lineHeight: 1.55,
          color: "var(--color-stone)",
        }}
      >
        Apparent temperature, coordinates, elevation, and pass label
        appear here.
      </p>
    </div>
  );
}

interface InspectDetailsProps {
  face: SelectedFace;
  onClear: () => void;
}

function InspectDetails({ face, onClear }: InspectDetailsProps) {
  return (
    <div className="space-y-4">
      <div>
        <div
          style={{
            fontSize: "10px",
            color: "var(--color-stone)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            fontWeight: 600,
            marginBottom: "6px",
          }}
        >
          Apparent temperature
        </div>
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "32px",
            color: "var(--color-ink)",
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1.1,
          }}
        >
          {face.apparentTempC !== null
            ? `${face.apparentTempC.toFixed(2)} °C`
            : "—"}
        </div>
        {face.apparentTempC === null && (
          <p
            style={{
              fontSize: "10px",
              color: "var(--color-stone)",
              marginTop: "4px",
            }}
          >
            No thermal coverage on this surface.
          </p>
        )}
      </div>

      {face.isLowEmissivity && (
        <div
          className="rounded-md p-3"
          style={{
            background: "rgba(196, 122, 42, 0.08)",
            border: "1px solid rgba(196, 122, 42, 0.25)",
            fontSize: "11px",
            lineHeight: 1.5,
            color: "var(--color-slate)",
          }}
        >
          <strong style={{ color: "var(--color-copper)" }}>
            Reflective surface
          </strong>{" "}
          — flagged by the operator. The temperature shown is what the
          camera captured; readings on bare metal need engineering
          judgement for absolute accuracy.
        </div>
      )}

      <DetailRow
        label="Coordinates"
        value={
          face.latLon
            ? `${face.latLon[0].toFixed(5)}°, ${face.latLon[1].toFixed(5)}°`
            : "—"
        }
      />
      <DetailRow
        label="Elevation"
        value={
          face.elevationM !== null ? `${face.elevationM.toFixed(2)} m` : "—"
        }
      />

      <button
        type="button"
        onClick={onClear}
        className="w-full transition-colors"
        style={{
          padding: "8px 0",
          fontSize: "11px",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          background: "transparent",
          color: "var(--color-stone)",
          border: "1px solid var(--color-mist)",
          borderRadius: "4px",
          cursor: "pointer",
          marginTop: "4px",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = "var(--color-slate)";
          e.currentTarget.style.borderColor = "var(--color-stone)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "var(--color-stone)";
          e.currentTarget.style.borderColor = "var(--color-mist)";
        }}
      >
        Clear selection
      </button>
    </div>
  );
}

interface DetailRowProps {
  label: string;
  value: string;
}

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div
      className="flex justify-between items-baseline gap-3"
      style={{
        paddingBottom: "8px",
        borderBottom: "1px solid var(--color-mist)",
      }}
    >
      <span
        style={{
          fontSize: "10px",
          color: "var(--color-stone)",
          textTransform: "uppercase",
          letterSpacing: "0.10em",
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: "12px",
          color: "var(--color-ink)",
          fontVariantNumeric: "tabular-nums",
          textAlign: "right",
        }}
      >
        {value}
      </span>
    </div>
  );
}
